import { z } from 'zod';
import {
  WorkspaceId,
  ArtistId,
  GigId,
  ChannelId,
  DeliveryId,
  PaymentConnectionId,
  TipId,
  AudienceSessionId,
} from '../ids.js';
import {
  IsoTimestamp,
  Version,
  Timestamps,
  CurrencyCode,
  MinorUnits,
  HttpsUrl,
  SourceCode,
} from '../primitives.js';
import {
  NotificationProvider,
  NotificationChannelStatus,
  DeliveryStatus,
  PaymentProvider,
  PaymentConnectionStatus,
  TipStatus,
} from '../enums.js';

/**
 * §27.17 NotificationChannel. `secretRef` points at Secrets Manager/SSM — never the value.
 * `safeConfig` holds only non-secret host/topic-fingerprint data safe to log (§21.2, §30.9).
 */
export const NotificationChannel = Timestamps.extend({
  channelId: ChannelId,
  workspaceId: WorkspaceId,
  artistId: ArtistId.nullable().default(null),
  provider: NotificationProvider,
  name: z.string().min(1).max(120),
  status: NotificationChannelStatus,
  secretRef: z.string().nullable().default(null),
  safeConfig: z.object({
    host: z.string().optional(),
    topicFingerprint: z.string().optional(),
    priority: z.number().int().min(1).max(5).optional(),
  }).default({}),
  eventPolicy: z.record(z.string(), z.unknown()).default({}),
  lastTestAt: IsoTimestamp.nullable().default(null),
  lastSuccessAt: IsoTimestamp.nullable().default(null),
  lastFailure: z
    .object({ at: IsoTimestamp, code: z.string() })
    .nullable()
    .default(null),
  version: Version,
});
export type NotificationChannel = z.infer<typeof NotificationChannel>;

/** §27.18 NotificationDelivery. Preview is safe/truncated; no raw audience content (§21.2). */
export const NotificationDelivery = z.object({
  deliveryId: DeliveryId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  gigId: GigId.nullable().default(null),
  channelId: ChannelId,
  eventId: z.string(),
  eventType: z.string(),
  idempotencyKey: z.string(),
  status: DeliveryStatus.default('pending'),
  attemptCount: z.number().int().nonnegative().default(0),
  safeRenderedPreview: z.string().max(280).optional(),
  providerResponseCode: z.string().nullable().default(null),
  nextAttemptAt: IsoTimestamp.nullable().default(null),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
});
export type NotificationDelivery = z.infer<typeof NotificationDelivery>;

/** §27.19 PaymentConnection. Stores only the connected-account reference and readiness (§22.3). */
export const PaymentConnection = Timestamps.extend({
  paymentConnectionId: PaymentConnectionId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  provider: PaymentProvider,
  providerAccountRef: z.string().nullable().default(null),
  status: PaymentConnectionStatus.default('disconnected'),
  capabilities: z.record(z.string(), z.boolean()).default({}),
  currencies: z.array(CurrencyCode).default([]),
  safeMetadata: z.record(z.string(), z.unknown()).default({}),
  lastProviderUpdateAt: IsoTimestamp.nullable().default(null),
  version: Version,
});
export type PaymentConnection = z.infer<typeof PaymentConnection>;

/** §27.20 Tip. Success is set only from a verified webhook (§22.6). */
export const Tip = z.object({
  tipId: TipId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  gigId: GigId,
  audienceSessionId: AudienceSessionId.nullable().default(null),
  provider: PaymentProvider,
  providerCheckoutRef: z.string().nullable().default(null),
  providerPaymentRef: z.string().nullable().default(null),
  currency: CurrencyCode,
  amountMinor: MinorUnits.positive(),
  applicationFeeMinor: MinorUnits.nonnegative().nullable().default(null),
  status: TipStatus.default('created'),
  message: z.string().max(280).nullable().default(null),
  sourceCode: SourceCode.nullable().default(null),
  createdAt: IsoTimestamp,
  succeededAt: IsoTimestamp.nullable().default(null),
  refundedAmountMinor: MinorUnits.nonnegative().default(0),
  disputeStatus: z.string().nullable().default(null),
  updatedAt: IsoTimestamp,
  version: Version,
});
export type Tip = z.infer<typeof Tip>;

/** External tip link config (§22.7). Records only a click event, never success. */
export const ExternalTipLink = z.object({
  method: z.enum(['paypal', 'venmo', 'cashapp', 'interac', 'custom']),
  label: z.string().min(1).max(60),
  url: HttpsUrl.optional(),
  instructions: z.string().max(280).optional(),
  enabled: z.boolean().default(true),
});
export type ExternalTipLink = z.infer<typeof ExternalTipLink>;
